import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Anchor,
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  MapPin,
  Loader2,
  Euro,
  Navigation,
  Settings,
  Moon,
  Repeat,
  Users
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/config/api";

export default function TariffsPage() {
  const [tariffs, setTariffs] = useState([]);
  const [pricingSettings, setPricingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  
  // Modal states
  const [tariffModalOpen, setTariffModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  
  // Form states
  const [tariffForm, setTariffForm] = useState({
    terminal_id: "",
    name: "",
    base_price: "",
    latitude: "",
    longitude: "",
    active: true
  });
  
  const [settingsForm, setSettingsForm] = useState({
    night_surcharge_percent: 20,
    night_start_hour: 22,
    night_end_hour: 6,
    round_trip_discount_percent: 10,
    extra_passenger_surcharge: 0
  });

  const fetchTariffs = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [tariffsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/tariffs`, { headers }),
        axios.get(`${API_URL}/api/admin/pricing-settings`, { headers })
      ]);
      
      setTariffs(tariffsRes.data.tariffs || []);
      setPricingSettings(settingsRes.data);
      setSettingsForm({
        night_surcharge_percent: settingsRes.data.night_surcharge_percent || 20,
        night_start_hour: settingsRes.data.night_start_hour || 22,
        night_end_hour: settingsRes.data.night_end_hour || 6,
        round_trip_discount_percent: settingsRes.data.round_trip_discount_percent || 10,
        extra_passenger_surcharge: settingsRes.data.extra_passenger_surcharge || 0
      });
    } catch (error) {
      console.error("Error fetching tariffs:", error);
      toast.error("Failed to load tariffs");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  const openTariffModal = (tariff = null) => {
    if (tariff) {
      setEditingTariff(tariff);
      setTariffForm({
        terminal_id: tariff.terminal_id,
        name: tariff.name,
        base_price: tariff.base_price.toString(),
        latitude: tariff.latitude?.toString() || "",
        longitude: tariff.longitude?.toString() || "",
        active: tariff.active
      });
    } else {
      setEditingTariff(null);
      setTariffForm({
        terminal_id: "",
        name: "",
        base_price: "",
        latitude: "",
        longitude: "",
        active: true
      });
    }
    setTariffModalOpen(true);
  };

  const closeTariffModal = () => {
    setTariffModalOpen(false);
    setEditingTariff(null);
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("La géolocalisation n'est pas supportée");
      return;
    }
    
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setTariffForm(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
        toast.success("Position détectée !");
        setDetectingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Impossible de détecter la position");
        setDetectingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const saveTariff = async () => {
    if (!tariffForm.terminal_id || !tariffForm.name || !tariffForm.base_price) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = {
        terminal_id: tariffForm.terminal_id,
        name: tariffForm.name,
        base_price: parseFloat(tariffForm.base_price),
        latitude: tariffForm.latitude ? parseFloat(tariffForm.latitude) : null,
        longitude: tariffForm.longitude ? parseFloat(tariffForm.longitude) : null,
        active: tariffForm.active
      };
      
      if (editingTariff) {
        await axios.put(`${API_URL}/api/admin/tariffs/${editingTariff.id}`, data, { headers });
        toast.success("Tarif mis à jour");
      } else {
        await axios.post(`${API_URL}/api/admin/tariffs`, data, { headers });
        toast.success("Tarif créé");
      }
      
      closeTariffModal();
      fetchTariffs();
    } catch (error) {
      console.error("Error saving tariff:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteTariff = async (tariff) => {
    if (!confirm(`Supprimer le tarif "${tariff.name}" ?`)) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/tariffs/${tariff.id}`, { headers });
      toast.success("Tarif désactivé");
      fetchTariffs();
    } catch (error) {
      console.error("Error deleting tariff:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/admin/pricing-settings`, {
        night_surcharge_percent: parseFloat(settingsForm.night_surcharge_percent),
        night_start_hour: parseInt(settingsForm.night_start_hour),
        night_end_hour: parseInt(settingsForm.night_end_hour),
        round_trip_discount_percent: parseFloat(settingsForm.round_trip_discount_percent),
        extra_passenger_surcharge: parseFloat(settingsForm.extra_passenger_surcharge)
      }, { headers });
      
      toast.success("Paramètres mis à jour");
      setSettingsModalOpen(false);
      fetchTariffs();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-4 shadow-lg">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="flex items-center gap-2 hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Y GO Admin</h1>
                <p className="text-xs text-slate-400">Gestion des Tarifs</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => setSettingsModalOpen(true)}
            variant="outline"
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Pricing Settings Summary */}
        {pricingSettings && (
          <Card className="mb-6 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-600" />
                  <span className="text-slate-600">Nuit:</span>
                  <span className="font-semibold">+{pricingSettings.night_surcharge_percent}%</span>
                  <span className="text-slate-500">({pricingSettings.night_start_hour}h-{pricingSettings.night_end_hour}h)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-green-600" />
                  <span className="text-slate-600">Aller-retour:</span>
                  <span className="font-semibold">-{pricingSettings.round_trip_discount_percent}%</span>
                </div>
                {pricingSettings.extra_passenger_surcharge > 0 && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-slate-600">Par passager:</span>
                    <span className="font-semibold">+€{pricingSettings.extra_passenger_surcharge}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tariffs Grid */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-800">Terminaux & Tarifs</h2>
          <Button onClick={() => openTariffModal()} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Terminal
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tariffs.map((tariff) => (
            <Card 
              key={tariff.id} 
              className={`relative ${!tariff.active ? 'opacity-60 bg-slate-100' : ''}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{tariff.name}</CardTitle>
                    <CardDescription className="font-mono text-xs">{tariff.terminal_id}</CardDescription>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openTariffModal(tariff)}
                    >
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => deleteTariff(tariff)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-orange-600">
                    €{tariff.base_price.toFixed(2)}
                  </div>
                  {tariff.latitude && tariff.longitude && (
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />
                      <span>{tariff.latitude.toFixed(4)}, {tariff.longitude.toFixed(4)}</span>
                    </div>
                  )}
                </div>
                {!tariff.active && (
                  <div className="mt-2 text-xs text-red-500 font-medium">⚠️ Inactif</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Tariff Modal */}
      <Dialog open={tariffModalOpen} onOpenChange={setTariffModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTariff ? "Modifier le Terminal" : "Nouveau Terminal"}
            </DialogTitle>
            <DialogDescription>
              {editingTariff 
                ? "Modifiez les informations du terminal"
                : "Ajoutez un nouveau terminal avec son tarif"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="terminal_id">ID Terminal *</Label>
                <Input
                  id="terminal_id"
                  value={tariffForm.terminal_id}
                  onChange={(e) => setTariffForm({...tariffForm, terminal_id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                  placeholder="ex: sogestrol"
                  disabled={!!editingTariff}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="base_price">Prix de base (€) *</Label>
                <Input
                  id="base_price"
                  type="number"
                  step="0.01"
                  value={tariffForm.base_price}
                  onChange={(e) => setTariffForm({...tariffForm, base_price: e.target.value})}
                  placeholder="40.00"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="name">Nom du terminal *</Label>
              <Input
                id="name"
                value={tariffForm.name}
                onChange={(e) => setTariffForm({...tariffForm, name: e.target.value})}
                placeholder="ex: Terminal Sogestrol"
                className="mt-1"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Coordonnées GPS (Geofencing)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={detectLocation}
                  disabled={detectingLocation}
                >
                  {detectingLocation ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Navigation className="w-4 h-4 mr-1" />
                  )}
                  Détecter ma position
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude" className="text-xs text-slate-500">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="0.000001"
                    value={tariffForm.latitude}
                    onChange={(e) => setTariffForm({...tariffForm, latitude: e.target.value})}
                    placeholder="49.4759"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude" className="text-xs text-slate-500">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="0.000001"
                    value={tariffForm.longitude}
                    onChange={(e) => setTariffForm({...tariffForm, longitude: e.target.value})}
                    placeholder="0.1855"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="active"
                checked={tariffForm.active}
                onCheckedChange={(checked) => setTariffForm({...tariffForm, active: checked})}
              />
              <Label htmlFor="active">Terminal actif</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeTariffModal}>
              Annuler
            </Button>
            <Button onClick={saveTariff} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingTariff ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Paramètres de Tarification</DialogTitle>
            <DialogDescription>
              Configurez les suppléments et réductions globales
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Night Surcharge */}
            <div className="space-y-3 p-4 bg-indigo-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Moon className="w-5 h-5 text-indigo-600" />
                <Label className="text-base font-semibold">Supplément de Nuit</Label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Pourcentage</Label>
                  <div className="flex items-center mt-1">
                    <Input
                      type="number"
                      value={settingsForm.night_surcharge_percent}
                      onChange={(e) => setSettingsForm({...settingsForm, night_surcharge_percent: e.target.value})}
                      className="w-full"
                    />
                    <span className="ml-1 text-slate-500">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Début (heure)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={settingsForm.night_start_hour}
                    onChange={(e) => setSettingsForm({...settingsForm, night_start_hour: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Fin (heure)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={settingsForm.night_end_hour}
                    onChange={(e) => setSettingsForm({...settingsForm, night_end_hour: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Round Trip Discount */}
            <div className="space-y-3 p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Repeat className="w-5 h-5 text-green-600" />
                <Label className="text-base font-semibold">Réduction Aller-Retour</Label>
              </div>
              <div className="flex items-center">
                <Input
                  type="number"
                  value={settingsForm.round_trip_discount_percent}
                  onChange={(e) => setSettingsForm({...settingsForm, round_trip_discount_percent: e.target.value})}
                  className="w-24"
                />
                <span className="ml-2 text-slate-500">% de réduction sur le retour</span>
              </div>
            </div>
            
            {/* Extra Passenger */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <Label className="text-base font-semibold">Supplément Passager</Label>
              </div>
              <div className="flex items-center">
                <span className="mr-2 text-slate-500">€</span>
                <Input
                  type="number"
                  step="0.01"
                  value={settingsForm.extra_passenger_surcharge}
                  onChange={(e) => setSettingsForm({...settingsForm, extra_passenger_surcharge: e.target.value})}
                  className="w-24"
                />
                <span className="ml-2 text-slate-500">par passager supplémentaire</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveSettings} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
